/**
 * Convert a client — FR-B8, ADR-0015 §4 as amended by ADR-0016 §4. On the
 * kit's fields and the Branches dialog's layout since Wave 15B (C14 §10.6
 * item 7; T-6.14); what it sends is unchanged.
 *
 * **In place. Nothing moves and nothing is created.** The dialog says so, in
 * those words, because the firm is about to change what a record *is* and the
 * obvious fear is that their cases and invoices are going somewhere. They are
 * not: the same record comes back with new values and one more line in its
 * history.
 *
 * The subtle part is the PAN. The service reads the body with `exclude_unset`,
 * so **an omitted `pan` keeps the client's PAN and an explicit `null` clears
 * it** — and this dialog therefore tracks whether the firm *touched* the field
 * rather than what it currently contains. A form that always sent `pan` would
 * wipe the identifier of every client converted without the field being filled
 * in. `convertBody` is where that lives, and it is tested.
 *
 * **A PAN kept must still fit** (ADR-0057 §4, C27): converting an individual
 * into a company while keeping their `P` PAN is refused by the service, and
 * the dialog says so under the PAN first — the firm replaces it or clears it.
 */

import { Button, Dialog, Input, Select } from '@fiveox/ui'
import { useState } from 'react'

import { DialogActions, FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  COMPANY_TYPE_LABELS,
  CONSTITUTION_LABELS,
  type ClientRecord,
  clients,
  companyTypeApplies,
  convertBody,
  describeKind,
  panHolderProblem,
  refusedField,
} from '../../lib/api/clients'

export function ConvertDialog({
  client,
  onClose,
  onConverted,
}: {
  client: ClientRecord
  onClose: () => void
  onConverted: () => void
}) {
  // A business converting stays a business (ADR-0016 §4 has no path back), so
  // the only choice offered is the constitution. An individual is converting to
  // a business, which is the flow the ADR is about.
  const [constitution, setConstitution] = useState<string>(
    client.constitution === 'proprietorship' ? 'partnership' : 'proprietorship',
  )
  const [companyType, setCompanyType] = useState<string | null>(null)

  const [pan, setPan] = useState(client.pan ?? '')
  const [panTouched, setPanTouched] = useState(false)
  const [gstin, setGstin] = useState(client.gstin ?? '')
  const [gstinTouched, setGstinTouched] = useState(false)

  const [error, setError] = useState<string | null>(null)
  const [refusedPan, setRefusedPan] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // The PAN the client will hold after this: the typed one once touched, else
  // the one it has. Either way it must fit the new constitution.
  const holder = panHolderProblem(panTouched ? pan : client.pan, 'business', constitution)
  const panError =
    refusedPan ??
    (holder && !panTouched ? `${holder} Replace the PAN or clear it to convert.` : holder)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (holder) return
    setSaving(true)
    setError(null)
    setRefusedPan(null)
    try {
      await clients.convert(
        client.id,
        convertBody(
          {
            kind: 'business',
            constitution,
            company_type: companyTypeApplies('business', constitution) ? companyType : null,
          },
          { panTouched, pan, gstinTouched, gstin },
        ),
      )
      onConverted()
    } catch (err) {
      const onField = refusedField(err)
      if (onField?.field === 'pan') setRefusedPan(onField.message)
      else setError(err instanceof ApiError ? err.message : 'The conversion could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} title="Convert this client" error={error} width="lg">
      <form onSubmit={submit}>
        <p className="text-muted-foreground mb-5 text-sm">
          {client.legal_name} is {describeKind(client).toLowerCase()}. Converting changes what
          this record is — <strong className="text-foreground">nothing moves and nothing new is
          created</strong>. Their cases, documents and ledger stay on this record, and the change is
          kept in the client’s history.
        </p>

        <div className={FORM_GRID}>
          <FormRow label="Becomes a" htmlFor="convert-constitution" required>
            <Select
              id="convert-constitution"
              value={constitution}
              aria-required="true"
              onChange={(e) => {
                setConstitution(e.target.value)
                setCompanyType(null)
              }}
              hint={
                constitution === 'proprietorship'
                  ? 'A proprietorship trades on the proprietor’s own PAN, so the PAN below usually stays as it is.'
                  : undefined
              }
            >
              {Object.entries(CONSTITUTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormRow>

          {companyTypeApplies('business', constitution) && (
            <FormRow label="Company type" htmlFor="convert-company-type">
              <Select
                id="convert-company-type"
                value={companyType ?? ''}
                onChange={(e) => setCompanyType(e.target.value || null)}
              >
                <option value="">Not stated</option>
                {Object.entries(COMPANY_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormRow>
          )}

          <FormRow label="PAN" htmlFor="convert-pan">
            <Input
              id="convert-pan"
              value={pan}
              onChange={(e) => {
                setPan(e.target.value)
                setPanTouched(true)
                setRefusedPan(null)
              }}
              maxLength={10}
              className="font-mono uppercase"
              error={panError}
              hint={
                panTouched
                  ? pan.trim()
                    ? 'The previous PAN is kept in this client’s history.'
                    : 'The PAN will be removed from this record, and kept in its history.'
                  : 'Leave this alone to keep the PAN as it is.'
              }
            />
          </FormRow>

          <FormRow label="GSTIN" htmlFor="convert-gstin">
            <Input
              id="convert-gstin"
              value={gstin}
              onChange={(e) => {
                setGstin(e.target.value)
                setGstinTouched(true)
              }}
              maxLength={15}
              className="font-mono uppercase"
            />
          </FormRow>
        </div>

        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={holder !== null} loading={saving} loadingText="Converting…">
            Convert
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
